import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const money = (v) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(Number(v) || 0);
const formatDate = (s) => s ? new Date(s).toLocaleString('en-ZA') : '—';

const STATUS_COLORS = {
  PENDING_PAYMENT: '#7f8c8d',
  PAYMENT_CONFIRMED: '#2980b9',
  DELIVERED: '#8e44ad',
  COMPLETED: '#27ae60',
  CANCELLED: '#95a5a6',
  REFUNDED: '#e67e22',
  FAILED: '#e74c3c',
};

const FILTERS = [
  { key: 'paid', label: 'Paid', match: (o) => o.paymentStatus === 'CAPTURED' },
  { key: 'pending', label: 'Awaiting payment', match: (o) => o.orderStatus === 'PENDING_PAYMENT' },
  { key: 'problems', label: 'Problems', match: (o) => o.shipmentStatus === 'FAILED' || o.payoutStatus === 'FAILED' || o.payoutStatus === 'MANUAL_REQUIRED' },
  { key: 'all', label: 'All', match: () => true },
];

const PaymentsPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('paid');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/payments');
      setOrders(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load payments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => orders.filter(FILTERS.find(f => f.key === filter).match),
    [orders, filter]
  );

  const totals = useMemo(() => visible.reduce((t, o) => ({
    items: t.items + (Number(o.itemPrice) || 0),
    shipping: t.shipping + (Number(o.shippingCost) || 0),
    total: t.total + (Number(o.totalAmount) || 0),
    fees: t.fees + (Number(o.platformFee) || 0),
    payouts: t.payouts + (Number(o.sellerPayout) || 0),
  }), { items: 0, shipping: 0, total: 0, fees: 0, payouts: 0 }), [visible]);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Payments</h2>
          <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '13px' }}>
            The 200 most recent orders. Amounts are what the buyer was charged and how it is split.
          </p>
        </div>
        <button onClick={load} style={{ padding: '8px 16px', cursor: 'pointer' }}>Refresh</button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{ ...btn, backgroundColor: filter === f.key ? '#3498db' : '#ecf0f1', color: filter === f.key ? '#fff' : '#2c3e50' }}
          >
            {f.label} ({orders.filter(f.match).length})
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          ['Items', totals.items],
          ['Shipping', totals.shipping],
          ['Buyers paid', totals.total],
          ['Eduthrift fees (escrow & buyer protection)', totals.fees],
          ['Seller payouts', totals.payouts],
        ].map(([label, value]) => (
          <div key={label} style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '12px', color: '#7f8c8d', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#2c3e50', marginTop: '4px' }}>{money(value)}</div>
          </div>
        ))}
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {loading ? (
        <p>Loading...</p>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          <p style={{ fontSize: '18px' }}>No orders in this view</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={th}>Order</th>
                <th style={th}>Buyer / Seller</th>
                <th style={{ ...th, textAlign: 'right' }}>Items</th>
                <th style={{ ...th, textAlign: 'right' }}>Shipping</th>
                <th style={{ ...th, textAlign: 'right' }}>Buyer paid</th>
                <th style={{ ...th, textAlign: 'right' }}>Fee</th>
                <th style={{ ...th, textAlign: 'right' }}>Seller gets</th>
                <th style={th}>Status</th>
                <th style={th}>Shipment</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(o => (
                <tr key={o.orderNumber} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td}>
                    <strong>{o.orderNumber}</strong><br />
                    <span style={muted}>{o.itemName}</span><br />
                    <span style={muted}>{formatDate(o.createdAt)}</span>
                  </td>
                  <td style={td}>
                    {o.buyer}<br /><span style={muted}>{o.buyerEmail}</span><br />
                    → {o.seller}<br /><span style={muted}>{o.sellerEmail}</span>
                  </td>
                  <td style={num}>{money(o.itemPrice)}</td>
                  <td style={num}>{money(o.shippingCost)}</td>
                  <td style={{ ...num, fontWeight: 600 }}>{money(o.totalAmount)}</td>
                  <td style={num}>{money(o.platformFee)}</td>
                  <td style={num}>{money(o.sellerPayout)}</td>
                  <td style={td}>
                    <span style={{ ...pill, backgroundColor: STATUS_COLORS[o.orderStatus] || '#7f8c8d' }}>{o.orderStatus}</span><br />
                    <span style={muted}>Payment {o.paymentStatus} · Escrow {o.escrowStatus} · Payout {o.payoutStatus}</span>
                    {o.payoutFailureReason && <div style={errText}>Payout: {o.payoutFailureReason}</div>}
                  </td>
                  <td style={td}>
                    <span style={{ ...pill, backgroundColor: o.shipmentStatus === 'FAILED' ? '#e74c3c' : o.shipmentStatus === 'CREATED' ? '#27ae60' : '#95a5a6' }}>
                      {o.shipmentStatus || '—'}
                    </span>
                    {o.trackingNumber && <div style={muted}>Waybill {o.trackingNumber}</div>}
                    {o.pickupPoint && <div style={muted}>Locker: {o.pickupPoint}</div>}
                    {o.shipmentFailureReason && <div style={errText}>{o.shipmentFailureReason}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', verticalAlign: 'top' };
const num = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const muted = { color: '#888', fontSize: '12px' };
const errText = { color: '#c0392b', fontSize: '12px', marginTop: '4px', maxWidth: '280px', wordBreak: 'break-word' };
const pill = { display: 'inline-block', color: '#fff', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 };
const btn = { padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' };

export default PaymentsPage;
